import React, { useEffect, useState } from "react";
import {
  CardContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetIntervelWiseInboundCallsReportQuery,
  useGetAllQueueQuery,
  useLazyGetIntervalWiseInboundCallsReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDate = (date: string) => date === getTodayDateString();

type ReportMode = "live" | "historical" | null;

const InboundCallDetailReport = () => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState<string>("");
  const [params, setParams] = useState<any>(null);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [reportFetching, setReportFetching] = useState(false);
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);
  const [reportMode, setReportMode] = useState<ReportMode>(null);

  const {
    data: intervalWiseInboundCallsReport,
    isFetching: liveReportFetching,
    status: liveReportStatus,
    error: intervalWiseInboundCallsReportError,
  } = useGetIntervelWiseInboundCallsReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerIntervalWiseInboundDownload, { isFetching: downloadLoading }] =
    useLazyGetIntervalWiseInboundCallsReportDownloadQuery();

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  const { data: queueData, isLoading: queueLoading } = useGetAllQueueQuery(undefined, {});
  const [selectedQueues, setSelectedQueues] = useState<any>([]);
  const [options, setOptions] = useState<any>([]);
  const [intervalValue, setIntervalValue] = useState("15");
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedHours, setSelectedHours] = useState<string>("");

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const columns: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr.No",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "queue_name",
      headerName: "Queue Name",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "interval",
      headerName: "Interval",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "calls_offered",
      headerName: "Calls Offered",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "unique_offered",
      headerName: "Unique Offered",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "answered",
      headerName: "Answered",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "unique_answered",
      headerName: "Unique Answered",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "not_answered",
      headerName: "Abondened",
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "unique_not_answered",
      headerName: "Unique Abondened",
      minWidth: 180,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "abandoned_within_30_seconds",
      headerName: "Abandoned (within 30 Seconds) ",
      minWidth: 286,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "abandoned_within_10_seconds",
      headerName: "Abandoned (within 10 Seconds)",
      minWidth: 286,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "abandoned_within_15_seconds",
      headerName: "Abandoned (within 15 Seconds)",
      minWidth: 286,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "abandoned_within_60_seconds",
      headerName: "Abandoned (within 60 Seconds) ",
      minWidth: 286,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "abandoned_percent",
      headerName: "Abandoned %",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "answered_percent",
      headerName: "Answered %",
      minWidth: 180,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "sl_percent",
      headerName: "Service Level(Including Abandoned)",
      minWidth: 140,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "slex_percent",
    //   headerName: "Service Level(Excluding Abandoned)",
    //   minWidth: 180,
    //   align: "center",
    //   headerAlign: "center",
    // },
  ];

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  useEffect(() => {
    if (queueData) {
      const transformedOptions = queueData.map((item) => ({
        label: item.name, // Use 'name' for the display text
        value: item.id, // Use 'id' as the unique identifier
      }));

      setOptions(transformedOptions);
    }
  }, [queueData]);

  const mapIntervalRow = (item: any, index: number, rowOffset: number) => {
    const sr = rowOffset + index + 1;
    const qn = item.queue_name;
    const queueDisplay = Array.isArray(qn)
      ? qn.join(", ")
      : qn != null && qn !== ""
        ? String(qn)
        : "-";
    return {
      id: sr,
      sr_no: sr,
      queue_name: queueDisplay,
      interval: item.interval,
      calls_offered: item.calls_offered,
      unique_offered: item.unique_offered,
      answered: item.answered,
      unique_answered: item.unique_answered,
      not_answered: item.not_answered,
      unique_not_answered: item.unique_not_answered,
      abandoned_within_30_seconds: item.abandoned_within_30_seconds,
      abandoned_within_10_seconds: item.abandoned_within_10_seconds,
      abandoned_within_15_seconds: item.abandoned_within_15_seconds,
      abandoned_within_60_seconds: item.abandoned_within_60_seconds,
      abandoned_percent: item.abandoned_percent,
      answered_percent: item.answered_percent,
      final_abandoned_count: item.final_abandoned_count,
      finalpercent: item.answered_percent,
      abandonedpercent: item.abandoned_percent,
      sl_percent: item.sl_percent,
      slex_percent: item.slex_percent,
    };
  };

  useEffect(() => {
    if (intervalWiseInboundCallsReportError) {
      if ("data" in intervalWiseInboundCallsReportError) {
        const errorMessage =
          intervalWiseInboundCallsReportError.data?.[0] ||
          "An unexpected error occurred.";
        toast.error(errorMessage);
        setLocalLoading(false);
      } else {
        toast.error("An unexpected error occurred.");
        setLocalLoading(false);
      }
    }
  }, [intervalWiseInboundCallsReportError]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (params) setLocalLoading(true);
  }, [params, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;

    if (intervalWiseInboundCallsReport === undefined) {
      setLocalLoading(false);
    } else if (Array.isArray(intervalWiseInboundCallsReport)) {
      const transformedRows = intervalWiseInboundCallsReport.map(
        (item, index) => ({
          id: index + 1,
          sr_no: index + 1,
          queue_name: Array.isArray(item.queue_name)
            ? item.queue_name.join(", ")
            : item.queue_name ?? "-",
          interval: item.interval,
          calls_offered: item.calls_offered,
          unique_offered: item.unique_offered,
          answered: item.answered,
          unique_answered: item.unique_answered,
          not_answered: item.not_answered,
          unique_not_answered: item.unique_not_answered,
          abandoned_within_30_seconds: item.abandoned_within_30_seconds,
          abandoned_within_10_seconds: item.abandoned_within_10_seconds,
          abandoned_within_15_seconds: item.abandoned_within_15_seconds,
          abandoned_within_60_seconds: item.abandoned_within_60_seconds,
          abandoned_percent: item.abandoned_percent,
          answered_percent: item.answered_percent,
          final_abandoned_count: item.final_abandoned_count,
          finalpercent: item.answered_percent,
          abandonedpercent: item.abandoned_percent,
          sl_percent: item.sl_percent,
          slex_percent: item.slex_percent,
        })
      );
      setDataRows(transformedRows);
    } else {
      setDataRows([]);
    }

    setLocalLoading(false);
  }, [intervalWiseInboundCallsReport, reportMode]);

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setTotalRowsFromApi(null);
      }
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      toast.error("Session expired or not logged in. Please sign in again.");
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      date: params.date,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      download: false,
      interval: params.interval,
    };
    if (params.queues != null && params.queues !== "") {
      queryParams.queues = params.queues;
    }
    if (params.wh === true) {
      queryParams.wh = true;
    }

    axios
      .get(`${reportingBaseUrl}/telephony/interval-wise-inbound-calls`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        const totalRows =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(
          typeof totalRows === "number" ? totalRows : results.length
        );
        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
        const rowOffset = (apiPage - 1) * pageSizeNum;
        if (results.length > 0) {
          setDataRows(
            results.map((item: any, index: number) =>
              mapIntervalRow(item, index, rowOffset)
            )
          );
        } else {
          setDataRows([]);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.[0] ||
          "Failed to fetch interval-wise inbound report";
        toast.error(typeof msg === "string" ? msg : "Request failed");
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number | boolean> = {
      date: params.date,
      page: 1,
      page_size: totalRowsFromApi ?? 99999,
      interval: params.interval,
    };
    if (params.queues != null && params.queues !== "") {
      downloadPayload.queues = params.queues;
    }
    if (params.wh === true) {
      downloadPayload.wh = true;
    }

    try {
      await triggerIntervalWiseInboundDownload(downloadPayload).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!dateFrom) {
      toast.error("Please select a date");
      return;
    }

    if (selectedQueues.length === 0) {
      toast.error("Please select a queue");
      return;
    }

    // Map the selectedQueues to extract the `label` values
    // const selectedQueueLabels = selectedQueues.map((queue) => queue.label);
    const selectedQueueLabels = selectedQueues
      .map((queueId) => {
        const queue = options.find((q) => q.value === queueId);
        return queue ? queue.label : null;
      })
      .filter(Boolean);

    const isLive = isCurrentDate(dateFrom);
    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });

    if (isLive) {
      setParams({
        gte: dateFrom,
        lte: dateFrom,
        interval: Number(intervalValue),
        queuename: selectedQueueLabels,
      });
      return;
    }

    const queryParams: any = {
      date: dateFrom,
      page: 1,
      page_size: paginationModel.pageSize,
      interval: Number(intervalValue),
      queues: selectedQueueLabels.join(","),
    };
    if (selectedHours === "non-working") {
      queryParams.wh = true;
    }
    setParams(queryParams);
  };

  const resetFilters = () => {
    setDateFrom("");
    setParams(null);
    setReportMode(null);
    setSelectedQueues([]);
    setDataRows([]);
    setTotalRowsFromApi(null);
    setSelectedHours("");
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params) return;
    if (reportMode === "live") return;
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const gridLoading =
    reportMode === "live" ? localLoading || liveReportFetching : localLoading;
  const showSpinner =
    queueLoading ||
    (reportMode === "live"
      ? liveReportStatus === "pending"
      : reportFetching || localLoading);

  const handleQueueChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes("all")) {
      setIsAllSelected(!isAllSelected);
      setSelectedQueues(
        isAllSelected ? [] : options.map((option) => option.value)
      );
    } else {
      setSelectedQueues(value);
      setIsAllSelected(value.length === options.length);
    }
  };

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        {/* Card Header */}
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* Back button */}
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/telephony/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            {" "}
            Interval-Wise Inbound Calls
          </span>
        </div>

        {/* Toolbar */}
        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="Date"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0],
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="Interval-select-label">Interval</InputLabel>
              <Select
                labelId="Interval-select-label"
                id="Interval-select"
                label="Interval"
                value={intervalValue}
                onChange={(e) => setIntervalValue(e.target.value)}
              >
                <MenuItem value="15">15</MenuItem>
                <MenuItem value="30">30</MenuItem>
                <MenuItem value="60">60</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* <div className="w-100 mw-150px">
            <MultiSelect
              options={options}
              value={selectedQueues}
              onChange={setSelectedQueues}
              labelledBy="Queue"
            />
          </div> */}
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="queue-select-label">Select Queues</InputLabel>
              <Select
                labelId="queue-select-label"
                id="queue-select"
                multiple
                value={selectedQueues}
                onChange={handleQueueChange}
                input={<OutlinedInput label="Select Queues" />}
                renderValue={(selected) => {
                  if (isAllSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((queueId) => {
                          const queue = options.find(
                            (q) => q.value === queueId
                          );
                          return queue ? queue.label : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() => setIsAllSelected(!isAllSelected)}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedQueues.length > 0 && !isAllSelected}
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {options.map((queue) => (
                  <MenuItem key={queue.value} value={queue.value}>
                    <Checkbox
                      checked={selectedQueues.indexOf(queue.value) > -1}
                    />
                    <ListItemText primary={queue.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="hours-select-label">Select Hours</InputLabel>
              <Select
                labelId="hours-select-label"
                id="hours-select"
                value={selectedHours}
                onChange={(e) => setSelectedHours(e.target.value)}
              >
                <MenuItem value="working">Working Hours</MenuItem>
                <MenuItem value="non-working">Non-Working Hours</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  filename="interval-wise-inbound-report"
                  datas={dataRows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? "Loading..." : "Download"}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>
        <CardContent style={{ width: "100%" }}>
          {showSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: totalRowsFromApi ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              // pageSizeOptions={[5, 10, 15, 20,25,30,35,40,45]}
              pageSizeOptions={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-columnsContainer": {
                  backgroundColor: "lightgray",
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};
export default InboundCallDetailReport;
